/**
 * NOS-67: Shared footer for all public nonprofit pages (subdomain, path-based, blog, events).
 *
 * NOS-218: field resolution (tagline, quick_links, social_links, contact, logo
 * override) is shared with the builder-canvas FooterSection via
 * src/lib/footerContent.ts so canvas and preview cannot drift.
 *
 * NOS-217: background comes from footer_content.background (imported from the
 * source site or set in the footer panel), defaulting to the original #0f0f14.
 * Foreground colors are luminance-derived (footerForeground) and exposed as
 * CSS variables so Tailwind hover states keep working; the logo renders via
 * FooterLogo (luminance-aware contrast chip).
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import {
  normalizeUrl,
  resolveFooterContent,
  footerVerticalPadding,
  socialIconForLabel,
  telHref,
  type FooterMovableGroup,
  type FooterSocialLink,
} from "@/lib/footerContent"
import type { ReactNode } from "react"
import { FooterLogo } from "@/components/public/FooterLogo"
import type { FooterSubscribeContent } from "@/lib/footerContent"

/**
 * NOS-232: live footer email capture for an imported newsletter block — wired
 * to the same submit-contact-form path as the NewsletterSignup block, never a
 * dead form. Carries data-footer-subscribe so the nav Subscribe CTA's
 * fragment-scroll fallback can land here.
 */
function FooterSubscribeForm({
  orgId,
  subscribe,
  primaryColor,
}: {
  orgId: string
  subscribe: FooterSubscribeContent
  primaryColor: string
}) {
  const [email, setEmail] = useState("")
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle")

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || state === "loading" || state === "done") return
    setState("loading")
    const { error } = await supabase.functions.invoke("submit-contact-form", {
      body: { org_id: orgId, form_data: { email: trimmed } },
    })
    setState(error ? "error" : "done")
  }

  return (
    <div data-footer-subscribe>
      <p className="text-[15px] font-semibold leading-snug text-[color:var(--nosf-heading)]">
        {subscribe.heading}
      </p>
      {subscribe.body && (
        <p className="mt-1.5 text-[13px] leading-relaxed text-[color:var(--nosf-body)]">
          {subscribe.body}
        </p>
      )}
      {state === "done" ? (
        <p className="mt-3 text-sm text-[color:var(--nosf-body)]">Thanks — you&apos;re subscribed!</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={subscribe.placeholder}
            className="min-w-0 flex-1 rounded-lg border border-[color:var(--nosf-border)] bg-transparent px-3 py-2 text-sm text-[color:var(--nosf-heading)] placeholder:text-[color:var(--nosf-icon)]"
          />
          <button
            type="submit"
            disabled={state === "loading"}
            className="rounded-lg px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: primaryColor }}
          >
            {state === "loading" ? "…" : subscribe.cta_text}
          </button>
        </form>
      )}
      {state === "error" && (
        <p className="mt-2 text-xs text-red-400">Something went wrong — please try again.</p>
      )}
    </div>
  )
}

export interface PublicFooterPageLink {
  id: string
  slug: string
  title: string | null
}

export interface PublicFooterProps {
  orgId: string
  orgName: string
  logoUrl?: string | null
  missionStatement?: string | null
  primaryColor: string
  secondaryColor?: string
  fontFamily: string
  pages: PublicFooterPageLink[]
  getPagePath: (slug: string) => string
  /** When true, hide "Powered by" (reserved for paid plans). MVP: always false from callers. */
  hidePoweredBy?: boolean
  /** Logo scale multiplier (default 1.0). Applied to the base footer logo height of 40px. */
  logoScale?: number
}

export function PublicFooter({
  orgId,
  orgName,
  logoUrl,
  missionStatement,
  primaryColor,
  secondaryColor = "#1e40af",
  fontFamily,
  pages,
  getPagePath,
  hidePoweredBy = false,
  logoScale = 1.0,
}: PublicFooterProps) {
  // Canonical source: org_customizations.footer_content (global, single source of truth).
  const [footerContent, setFooterContent] = useState<Record<string, unknown> | null>(null)
  // Legacy fallback (pre-migration orgs): social links from the home page's page_sections footer row.
  const [legacySocialLinks, setLegacySocialLinks] = useState<FooterSocialLink[]>([])

  useEffect(() => {
    let cancelled = false
    async function loadFooterContent() {
      // Primary: read from org_customizations.footer_content (global, single source of truth).
      const { data: cust } = await supabase
        .from("org_customizations")
        .select("footer_content")
        .eq("org_id", orgId)
        .maybeSingle()
      if (cancelled) return
      const fc = cust?.footer_content as Record<string, unknown> | null | undefined
      if (fc && typeof fc === "object") {
        setFooterContent(fc)
        return
      }
      // Fallback: legacy home-page page_sections footer row.
      const { data: home } = await supabase
        .from("pages")
        .select("id")
        .eq("org_id", orgId)
        .eq("slug", "home")
        .eq("is_published", true)
        .maybeSingle()
      if (cancelled || !home?.id) return
      const { data: sec } = await supabase
        .from("page_sections")
        .select("content")
        .eq("page_id", home.id)
        .eq("section_type", "footer")
        .order("order_index", { ascending: true })
        .limit(1)
        .maybeSingle()
      if (cancelled || !sec?.content || typeof sec.content !== "object") return
      const raw = (sec.content as Record<string, unknown>).social_links
      if (!Array.isArray(raw)) return
      const links = raw
        .map((item) => {
          if (!item || typeof item !== "object") return null
          const o = item as Record<string, unknown>
          const label = typeof o.label === "string" ? o.label : "Social"
          const url = typeof o.url === "string" ? normalizeUrl(o.url) : ""
          return url ? { label, url } : null
        })
        .filter(Boolean) as FooterSocialLink[]
      if (!cancelled) setLegacySocialLinks(links)
    }
    loadFooterContent()
    return () => {
      cancelled = true
    }
  }, [orgId])

  // NOS-218: shared resolution — identical rules to the builder canvas footer.
  const resolved = useMemo(
    () => resolveFooterContent(footerContent, { orgLogoUrl: logoUrl, missionStatement }),
    [footerContent, logoUrl, missionStatement]
  )
  const socialLinks = footerContent ? resolved.socialLinks : legacySocialLinks
  const contactInfo = resolved.contact

  // Use custom quick links from footer_content if set, otherwise fall back to auto-generated from pages.
  // NOS-265: trust the caller's order — every caller passes sortPages()-ordered
  // pages, matching the nav and the Astro renderer (which re-sorting to
  // alphabetical here silently broke).
  const autoQuickLinks = pages

  const year = new Date().getFullYear()

  // NOS-217: luminance-derived foreground palette as CSS variables so the
  // Tailwind color/hover classes below adapt to light imported backgrounds.
  const { fg } = resolved
  const footerStyle: CSSProperties = {
    background: resolved.background,
    fontFamily,
    color: fg.text,
    borderColor: fg.border,
    ["--nosf-heading" as string]: fg.heading,
    ["--nosf-body" as string]: fg.body,
    ["--nosf-muted" as string]: fg.muted,
    ["--nosf-subtle" as string]: fg.subtle,
    ["--nosf-faint" as string]: fg.faint,
    ["--nosf-icon" as string]: fg.icon,
    ["--nosf-border" as string]: fg.border,
  }

  // NOS-234: contact renders inside the Connect group when they share a column
  // (the historical layout); moved elsewhere it gets its own heading.
  const { columnLayout } = resolved
  const contactWithConnect =
    contactInfo != null && columnLayout.contact === columnLayout.connect

  const contactRows = contactInfo && (
    <div className="space-y-1">
      {contactInfo.email && (
        <p>
          <a
            href={`mailto:${contactInfo.email}`}
            className="text-sm text-[color:var(--nosf-muted)] transition hover:text-[color:var(--nosf-heading)]"
          >
            {contactInfo.email}
          </a>
        </p>
      )}
      {contactInfo.phone && (
        <p>
          <a
            href={telHref(contactInfo.phone)}
            className="text-sm text-[color:var(--nosf-muted)] transition hover:text-[color:var(--nosf-heading)]"
          >
            {contactInfo.phone}
          </a>
        </p>
      )}
      {contactInfo.address && (
        <p className="text-sm text-[color:var(--nosf-muted)]">{contactInfo.address}</p>
      )}
    </div>
  )

  const groupRenderers: Record<FooterMovableGroup, () => ReactNode> = {
    quick_links: () => (
      <div key="quick_links">
        <p
          className="text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: primaryColor }}
        >
          Quick links
        </p>
        <ul className="mt-4 space-y-2">
          {resolved.quickLinks !== null
            ? resolved.quickLinks.map((link) => (
                <li key={link.href}>
                  {link.open_in_new_tab || link.href.startsWith("http") ? (
                    <a
                      href={link.href}
                      target={link.open_in_new_tab ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="text-sm text-[color:var(--nosf-body)] transition hover:text-[color:var(--nosf-heading)]"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-[color:var(--nosf-body)] transition hover:text-[color:var(--nosf-heading)]"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))
            : autoQuickLinks.map((p) => {
                const path = getPagePath(p.slug)
                const label = p.title?.trim() || p.slug || "Page"
                return (
                  <li key={p.id}>
                    <Link
                      to={path}
                      className="text-sm text-[color:var(--nosf-body)] transition hover:text-[color:var(--nosf-heading)]"
                    >
                      {label}
                    </Link>
                  </li>
                )
              })}
        </ul>
      </div>
    ),
    connect: () => (
      <div key="connect">
        <p
          className="text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: primaryColor }}
        >
          Connect
        </p>
        <div className="mt-4 flex flex-wrap gap-4">
          {socialLinks.map((link) => {
            const IconComp = socialIconForLabel(link.label)
            return (
              <a
                key={`${link.label}-${link.url}`}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[color:var(--nosf-faint)] transition hover:text-[color:var(--nosf-body)]"
                aria-label={link.label}
              >
                {IconComp ? (
                  <IconComp className="h-5 w-5 shrink-0" strokeWidth={2} />
                ) : (
                  <span className="underline">{link.label}</span>
                )}
              </a>
            )
          })}
        </div>
        {socialLinks.length === 0 && !contactWithConnect && (
          <p className="mt-2 text-sm text-[color:var(--nosf-icon)]">Add social links in your site footer (builder).</p>
        )}
        {contactWithConnect && <div className="mt-4">{contactRows}</div>}
      </div>
    ),
    contact: () =>
      contactWithConnect || !contactInfo ? null : (
        <div key="contact">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: primaryColor }}
          >
            Contact
          </p>
          <div className="mt-4">{contactRows}</div>
        </div>
      ),
    subscribe: () =>
      !resolved.subscribe ? null : (
        <FooterSubscribeForm
          key="subscribe"
          orgId={orgId}
          subscribe={resolved.subscribe}
          primaryColor={primaryColor}
        />
      ),
  }

  const groupsIn = (col: 1 | 2 | 3): ReactNode[] =>
    (Object.keys(groupRenderers) as FooterMovableGroup[])
      .filter((g) => columnLayout[g] === col)
      .map((g) => groupRenderers[g]())
      .filter(Boolean)

  // NOS-234: Compact/Normal/Spacious from the footer panel (footer_content._styles).
  const paddingY = footerVerticalPadding(footerContent)

  return (
    <footer className="border-t" style={footerStyle}>
      <div
        className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
        style={{
          paddingTop: paddingY,
          paddingBottom: paddingY,
        }}
      >
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12">
          {/* Column 1: brand (fixed) + any groups assigned to column 1 */}
          <div className="flex flex-col gap-8">
            <div>
              {resolved.logoUrl ? (
                <FooterLogo
                  src={resolved.logoUrl}
                  alt={orgName}
                  height={Math.round(40 * logoScale)}
                  isUserOverride={resolved.logoIsOverride}
                  backgroundIsDark={resolved.backgroundIsDark}
                  treatment={resolved.logoTreatment}
                />
              ) : (
                <p className="text-lg font-semibold text-[color:var(--nosf-heading)]">{orgName}</p>
              )}
              {resolved.tagline && (
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--nosf-body)]">{resolved.tagline}</p>
              )}
            </div>
            {groupsIn(1)}
          </div>
          <div className="flex flex-col gap-8">{groupsIn(2)}</div>
          <div className="flex flex-col gap-8">{groupsIn(3)}</div>
        </div>
        <div
          className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-[color:var(--nosf-border)] pt-8 sm:flex-row"
        >
          <p className="text-sm text-[color:var(--nosf-subtle)]">
            © {year} {orgName}. All rights reserved.
          </p>
          {!hidePoweredBy && (
            <p className="text-xs text-[color:var(--nosf-faint)]">
              <a
                href="https://getnos.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-2 hover:underline"
                style={{ color: secondaryColor }}
              >
                Powered by NOS
              </a>
            </p>
          )}
        </div>
      </div>
    </footer>
  )
}
